import { useToast } from '@evisort/eds/components/Toast/hooks';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { DashboardType } from '~/enums/DashboardType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { preSigMocks, preSigMocksV2 } from '~/redux/api/methods/dashboardV2';
import { coerceRtkqError } from '~/redux/api/utils';
import { Dashboard as DashboardObjectType } from '~/types/Dashboard';

import { useCurrentUser } from './useCurrentUser';
import { usePermission } from './usePermission';

export const useDashboardsSubnavs = () => {
  const intl = useIntl();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const hasDashboardV3 = useFlag(FlagType.DashboardV3);
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  const hasClmR2TicketsDashboard = useFlag(FlagType.CLMR2TicketsDashboard);

  const {
    isLoading: isWorkflowDashboardPermissionLoading,
    hasPermission: hasWorkflowDashboardPermission,
  } = usePermission({
    permission: { resourceId: 'workflows_dashboard', resourceType: 'view' },
  });
  const {
    isLoading: isDocDashboardPermissionLoading,
    hasPermission: hasDocDashboardPermission,
  } = usePermission({
    permission: { resourceId: 'documents_dashboard', resourceType: 'view' },
  });
  const [loadLegacyDashboards, legacyDashboardsResults] =
    api.endpoints.getDashboards.useLazyQuery(undefined);
  const { data: legacyDashboards = [] } = legacyDashboardsResults;

  const [
    getDashboardsV2,
    {
      data: dashboardsResponse,
      isError: isFetchingDashboardsErrorV2,
      isSuccess: isFetchingDashboardsSuccessV2,
    },
  ] = api.endpoints.getDashboardsV2.useLazyQuery();

  const [createDashboards, createDefaultDashboardsResult] =
    api.endpoints.createDefaultDashboards.useLazyQuery(undefined);
  const {
    data: defaultDashboardsResponse,
    error,
    isSuccess: isCreatingDefaultDashboardsSuccess,
    isError: isCreatingDefaultDashboardsError,
  } = createDefaultDashboardsResult;

  const hasDefaultDashboards = dashboardsResponse?.results.some(
    (d: any) => d.is_default,
  );

  const isLoadingPermissions =
    isWorkflowDashboardPermissionLoading || isDocDashboardPermissionLoading;

  const hasDocumentDashboardPermission =
    !isDocDashboardPermissionLoading && hasDocDashboardPermission;

  const isDefaultDashboardsSet = !hasDocumentDashboardPermission
    ? true
    : hasDefaultDashboards ||
      isFetchingDashboardsErrorV2 ||
      (defaultDashboardsResponse?.length && isCreatingDefaultDashboardsSuccess);

  const isFetching = isLoadingPermissions || !isDefaultDashboardsSet;

  const checkIfLegacyDashboard = (i: DashboardObjectType) =>
    i['type'] === ('tickets' as DashboardType) ||
    i['type'] === ('workflows' as DashboardType);

  const resolvePreSigDashboards = () => {
    if (!hasDashboardV3 || !hasWorkflowDashboardPermission) {
      return legacyDashboards.filter(checkIfLegacyDashboard);
    }
    if (hasClmEvolutionPhase1 && hasClmR2TicketsDashboard) {
      return preSigMocksV2(currentUser, intl);
    }
    return preSigMocks(currentUser, intl);
  };
  const preSigDashboards = resolvePreSigDashboards();

  const canViewDashboards =
    !isLoadingPermissions &&
    (hasDocDashboardPermission || hasWorkflowDashboardPermission);

  useEffect(() => {
    if (!canViewDashboards) return;
    if (!hasDashboardV3) {
      loadLegacyDashboards(undefined);
    }
    getDashboardsV2({ page: 1, pageSize: 2, return_defaults: true });
  }, [
    canViewDashboards,
    hasDashboardV3,
    loadLegacyDashboards,
    getDashboardsV2,
  ]);

  useEffect(() => {
    if (
      hasDocumentDashboardPermission &&
      !!isFetchingDashboardsSuccessV2 &&
      dashboardsResponse?.results.length === 0
    ) {
      createDashboards(undefined);
    }
  }, [
    hasDocumentDashboardPermission,
    isFetchingDashboardsSuccessV2,
    dashboardsResponse,
    createDashboards,
  ]);

  useEffect(() => {
    if (isCreatingDefaultDashboardsSuccess && !!defaultDashboardsResponse) {
      getDashboardsV2({ page: 1, pageSize: 2, return_defaults: true });
    }
  }, [
    isCreatingDefaultDashboardsSuccess,
    defaultDashboardsResponse,
    getDashboardsV2,
  ]);

  useEffect(() => {
    if (isCreatingDefaultDashboardsError) {
      toast({
        status: 'critical',
        text:
          coerceRtkqError(error)?.response?.data?.errors?.[0]?.title ||
          intl.formatMessage({
            id: 'useDashboardsSubnavs.errorCreatingDashboard',
            defaultMessage: 'An error occurs when creating a dashboard.',
            description: 'Error message when dashboard creation fails',
          }),
      });
    }
  }, [isCreatingDefaultDashboardsError, error, intl, toast]);

  const dashboards = [
    ...(dashboardsResponse?.results || []),
    ...preSigDashboards,
  ];

  return {
    dashboards,
    isFetching,
  };
};
